<template>
  <BModal
    v-model="isVisible"
    hide-footer
    hide-header
    size="lg"
    @hidden="handleClose"
  >
    <div class="day-order-modal">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="icon-circle">
            <i class="far fa-calendar-alt" />
          </div>

          <div class="header-text">
            <div
              class="day-title"
              :class="{ 'batch-title': batch }"
            >
              {{ formattedTitle }}
            </div>
            <div class="day-info">
              <span
                v-if="!batch"
                class="weekday"
              >
                {{ formattedWeekday }}
              </span>
              <span
                v-if="!batch"
                class="separator"
              >
                •
              </span>
              <span class="payer-name">
                {{ payer?.payerName }}
              </span>
            </div>
          </div>
        </div>

        <button
          class="close-button"
          @click="handleClose"
        >
          <i class="fas fa-times" />
        </button>
      </div>

      <!-- Content -->
      <div>
        <TabsOffer
          :payer="payer"
          :catalog="catalog"
          :batch="batch"
          :calendar="calendarOffer"
          :day="day"
          :orders="orders"
          :on-refresh="handleRefresh"
          :details="!batch"
        />
      </div>
    </div>

    <div>
      <button
        v-if="cancelledOrders.length > 0"
        class="btn btn-danger btn-block"
        :disabled="cancelPending"
        @click="confirmCancellation"
      >
        Potwierdź odwołanie
      </button>
      <button
        v-else
        class="btn btn-primary btn-block"
        @click="handleClose"
      >
        Zamknij
      </button>
    </div>
  </BModal>
</template>

<script>
import Bowser from 'bowser';
import axios from '@/api/axios';
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import TabsOffer from './TabsOffer';

export default {
  name: 'DayOrderModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    day: {
      type: String,
      required: true,
    },
    monthEnd: {
      type: String,
      default: null,
    },
    batch: {
      type: Boolean,
      default: false,
    },
    payer: {
      type: Object,
      required: true,
    },
    catalog: {
      type: Object,
      required: true,
    },
    offer: {
      type: Object,
      required: true,
    },
    orders: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localOrders: this.orders,
      pending: false,
      cancelPending: false,
    };
  },
  components: {
    TabsOffer,
  },
  computed: {
    ...mapGetters(['ordersRefresh']),
    ...mapGetters(['catalogBasket']),

    cancelledOrders() {
      return this.catalogBasket.filter((x) =>
        x.payerId === this.payer.payerId
         && x.day === this.day
         && x.isReturned);
    },

    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },

    formattedTitle() {
      if (this.batch) {
        return moment(this.day).format('MMMM');
      }
      return moment(this.day).format('DD MMMM');
    },

    formattedWeekday() {
      return moment(this.day).format('dddd');
    },

    calendarOffer() {
      if (this.batch) {
        return this.offer.calendar.filter((x) => x.canOrder);
      }
      return this.offer.calendar.filter((x) => x.day === this.day);
    },

    needsOrderRefresh() {
      return this.ordersRefresh.some(
        (x) => x.payerId === this.payer.payerId && x.catalogId === this.catalog.catalog.id,
      );
    },
  },
  watch: {
    orders(newOrders) {
      this.localOrders = newOrders;
    },

    needsOrderRefresh(newVal) {
      if (newVal) {
        this.fetchOrders();
      }
    },
  },
  methods: {
    ...mapActions([
      'createOrder',
    ]),
    confirmCancellation() {
      this.cancelPending = true;
      this.createOrder({
        params: {
          query: {
            payeeId: this.payer.payeeId,
          },
        },
        data: {
          isApp: false,
          origin: JSON.stringify(Bowser.parse(window.navigator.userAgent)),
          version: process.env.VUE_APP_VERSION || '?',
          catalogId: this.catalog.catalog.id,
          addedItems: [],
          cancelledItems: this.cancelledOrders.map((x) => ({
            availabilityId: x.offerId,
            payerId: x.payerId,
            day: x.day,
            count: x.count,
          })),
        },
      })
        .then(() => {
          this.cancelledOrders.forEach((x) => {
            this.$store.commit('removeFromBasket', {
              payerId: x.payerId,
              offerId: x.offerId,
              day: x.day,
            });
          });

          this.$root.$emit('order-created');
          this.handleClose();
        })
        .finally(() => {
          this.cancelPending = false;
        });
    },
    async fetchOrders() {
      try {
        this.pending = true;
        const response = await axios.get('/user/shop/orders', {
          params: {
            query: {
              payerId: this.payer.payerId,
              from: moment().startOf('month').format('YYYY-MM-DD'),
              to: moment().endOf('month').format('YYYY-MM-DD'),
              catalogId: this.catalog.catalog.id,
            },
          },
        });

        this.localOrders = response.data;

        // Mark orders as refreshed
        this.$store.commit('ordersRefreshed', {
          payerId: this.payer.payerId,
          catalogId: this.catalog.catalog.id,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to refresh orders:', error);
      } finally {
        this.pending = false;
      }
    },

    handleRefresh() {
      this.fetchOrders().catch(() => {});
    },

    showDay(offset) {
      const targetDay = moment(this.day, 'YYYY-MM-DD').add(offset, 'days');

      this.$emit('day-change', {
        day: targetDay.format('YYYY-MM-DD'),
        payer: this.payer,
        catalog: this.catalog,
        offer: this.offer,
        orders: this.localOrders,
      });
    },

    handleClose() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.day-order-modal {
  position: relative;
  min-height: 500px;
}

.modal-header {
  display: flex;
  align-items: center;
  padding: 10px 10px;
  border-bottom: 1px solid #e0e0e0;
}

.header-content {
  display: flex;
  align-items: center;
  flex-grow: 1;
}

.icon-circle {
  width: 50px;
  height: 50px;
  border-radius: 25px;
  background-color: rgba(0, 123, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;

  i {
    font-size: 25px;
    color: #007bff;
  }
}

.header-text {
  flex-grow: 1;
}

.day-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 20px;

  &.batch-title {
    text-transform: capitalize;
  }
}

.day-info {
  display: flex;
  align-items: center;
  margin-top: 5px;

  .weekday {
    text-transform: capitalize;
    color: #9e9e9e;
    font-size: 12px;
  }

  .separator {
    color: #9e9e9e;
    font-size: 12px;
    margin: 0 5px;
  }

  .payer-name {
    color: #9e9e9e;
    font-size: 12px;
  }
}

.close-button {
  position: absolute;
  right: 12px;
  top: 12px;
  background: none;
  border: none;
  font-size: 22px;
  color: #888;
  cursor: pointer;
  padding: 5px;

  &:hover {
    color: #666;
  }
}

// .modal-content {
//   flex-grow: 1;
//   overflow-y: auto;
//   max-height: 60vh;
//   margin-top: 10px;
// }

.modal-footer {
  border-top: 1px solid #e0e0e0;
  padding: 10px;
}
</style>
